<template>
  <form class="order-form">
    <h2>{{ $t('product-detail.form.header') }}</h2>
    <fieldset class="order-info">
      <div class="clearfix">
        <engraved-id :value="engravedId"
                     :validation="$v.engravedId"
                     :validate-on-blur="dataLoaded"
                     @input="value => setEngravedId(value)"/>
        <a href="https://www.ov-chipkaart.nl/persoonlijke-ov-chipkaart-aanvragen"
           class="no-ovck"
           target="_blank"
           rel="noopener">{{ $t('engraved-id.no-chipcard') }}</a>
      </div>
      <customer-group-form v-if="!isRegularProduct"
                           :customer-groups-to-show="customerGroupsToShow"
                           :selected-customer-group="customerGroup"
                           :validation="$v.customerGroup"
                           @input="value => setCustomerGroup(value)"/>

      <center-zone v-if="isStarProduct"
                   :zones="zones"
                   :selected-zone="zone"
                   :validation="$v.zone"
                   @input="value => setZone(value)"/>

      <star-value v-if="isStarProduct"
                  :star-values="starValues"
                  :selected-star-value="starValue"
                  :validation="$v.starValue"
                  @input="value => setStarValue( value)"/>

      <a v-if="isStarProduct"
         href="arriva://zone-map-for-current-selection"
         style="margin-left: 2.35em;">{{ $t('product-detail.form.star-value.zone-tool-link') }}</a>
      <br>

      <route v-if="isRouteProduct"
             :stops="stops"
             :to-stop="toStop"
             :from-stop="fromStop"
             :validation-to-stop="$v.toStop"
             :validation-from-stop="$v.fromStop"
             @inputToStopId="value => setToStop(value)"
             @inputFromStopId="value => setFromStop(value)"/>

      <class :classes="classes"
             :selected-class="selectedClass"
             :validation="$v.selectedClass"
             @input="value => setClass (value)"/>

      <term :terms="terms"
            :configurations="configurations"
            :customer-groups-to-show="customerGroupsToShow"
            :customer-group="customerGroup"
            :selected-term="term"
            :validation="$v.term"
            :is-star-product="isStarProduct"
            @input="value => setSelectedTerm(value)"/>

      <date-effective :min-date="minDate"
                      :max-date="maxDate"
                      :value="dateEffectiveAsDate"
                      :validation="$v.dateEffective"
                      @input="setDateEffective"/>

      <expiry-reminder v-if="term && supportsSubscriptions"
                       :value="expiryReminder"
                       @input="setExpiryReminder"/>

      <agree-to-terms :value="agreeToTerms"
                      :terms-link="product.termsLink"
                      :validation="$v.agreeToTerms"
                      @input="setAgreeToTerms"/>
    </fieldset>
    <fieldset class="submit">
      <form-row>
        <button :class="[{'added-to-cart': addToCartDisabled}, 'add-to-cart']"
                :disabled="addToCartDisabled"
                type="button"
                @click="validateForm">
          <span class="initial-state">
            <span class="icon-shopping-cart"/> {{ $t('product-detail.form.button-initial-state') }}
          </span>
          <span class="success-state">
            <span class="icon-check"/> {{ $t('product-detail.form.button-success-state') }}
          </span>
        </button>
        <button :disabled="!addToCartDisabled" type="button" class="link-to-cart"
                @click="goToCart">
          {{ $t('product-detail.form.button-to-cart') }}<span class="icon-chevron-right"/>
        </button>
      </form-row>
      <ul v-if="mixedShopItemsErrorCode" class="ui-state-error" role="alert" aria-atomic="true" aria-live="polite">
        <li v-html="$t('product-detail.form.validation.' + shopConfig.mixedShopError.message, { link: shopLink })"/>
      </ul>
    </fieldset>
  </form>
</template>

<script>
import { mapState } from 'vuex'
import { validationMixin, withParams } from 'vuelidate'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { regex } from 'vuelidate/lib/validators/common'
import minLength from 'vuelidate/lib/validators/minLength'
import { buildOrderItem } from '../types/order-item'
import { buildOrder } from '../types/order'
import { parseStoreDate, formatStoreDate, formatOrderApiDate } from '../utils/date'
import {
  CARD_NUMBER_INVALID,
  CARD_NOT_FOUND_AT_TLS,
  CARD_LOCALLY_BLACKLISTED,
  CARD_HAS_WRONG_TYPE,
  CARD_NOT_ACTIVE_AT_TLS,
  CUSTOMER_GROUP_NOT_MATCH_CARD_HOLDER,
  TARIFF_NOT_PERMITTED_FOR_CARD,
  NO_MATCHING_PRICE_FOR_CONFIGURATION,
  ORDER_HAS_DUPLICATE_ITEMS,
  ORDER_IS_MIXED,
  ORDER_NOT_ELIGIBLE_FOR_SPECIAL_SALE,
  ORDER_HAS_MIXED_SHOP_ITEMS,
  CARD_HAS_EXPIRED,
  BASE_PROPOSITION_ALREADY_REGISTERED,
  ORDER_HAS_PRODUCT_CONFLICT_CRITICAL,
  ORDER_HAS_PRODUCT_CONFLICT_HIGH,
  ORDER_HAS_PRODUCT_CONFLICT_MEDIUM
} from '../order-api/order-api-client-constants'
import {
  SET_SELECTED_TERM,
  SET_ENGRAVED_ID,
  SET_SELECTED_CUSTOMER_GROUP,
  SET_SELECTED_ZONE,
  SET_SELECTED_STAR_VALUE,
  SET_SELECTED_CLASS,
  SET_SELECTED_TO_STOP,
  SET_SELECTED_FROM_STOP,
  SET_DATE_EFFECTIVE,
  SET_EXPIRY_REMINDER, RESET
} from '../store/modules/configuration/mutation-types'
import AgreeToTerms from './order-item/AgreeToTerms'
import CenterZone from './order-item/CenterZone'
import Class from './order-item/Class'
import CustomerGroupForm from './order-item/CustomerGroup'
import DateEffective from './order-item/DateEffective'
import EngravedId from './order-item/EngravedId'
import ExpiryReminder from './order-item/ExpiryReminder'
import FormRow from './components/FormRow'
import Route from './order-item/Route'
import StarValue from './order-item/StarValue'
import Term from './order-item/Term'

export default {
  components: {
    AgreeToTerms,
    CenterZone,
    Class,
    CustomerGroupForm,
    DateEffective,
    EngravedId,
    ExpiryReminder,
    FormRow,
    Route,
    StarValue,
    Term
  },
  mixins: [validationMixin],
  props: {
    customerGroupsToShow: {
      type: Array,
      required: true
    },
    dataLoaded: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      orderApiErrorResponse: null,
      addToCartDisabled: false,
      satisfiableByCustomerGroup: null,
      agreeToTerms: false
    }
  },
  computed: {
    ...mapState({
      terms: state => state.configuration.availableFilterEntities.terms,
      term: state => state.configuration.selectedTerm,
      classes: state => state.configuration.availableFilterEntities.classes,
      selectedClass: state => state.configuration.selectedClass,
      starValues: state => state.configuration.availableFilterEntities.starValues,
      starValue: state => state.configuration.selectedStarValue,
      stops: state => state.configuration.availableFilterEntities.stops,
      fromStop: state => state.configuration.selectedFromStop,
      toStop: state => state.configuration.selectedToStop,
      zones: state => state.configuration.availableFilterEntities.zones,
      zone: state => state.configuration.selectedZone,
      configurations: state => state.configuration.productConfigurations,
      coveredPeriod: state => state.configuration.coveredPeriod,
      product: state => state.configuration.selectedProduct,
      engravedId: state => state.configuration.engravedId,
      renewSubscriptionId: state => state.configuration.renewSubscriptionId,
      dateEffective: state => state.configuration.dateEffective,
      expiryReminder: state => state.configuration.expiryReminder,
      specialSaleSlug: state => state.configuration.specialSaleSlug,
      shopConfig: state => state.shop.shopConfig
    }),
    customerGroup: function () {
      return this.$store.getters['configuration/customerGroupToUseForConfiguration']
    },
    dateEffectiveAsDate: function () {
      return parseStoreDate(this.dateEffective)
    },
    minDate: function () {
      if (!this.coveredPeriod) return null

      const minDate = new Date(this.coveredPeriod.start)
      const today = new Date()

      return minDate < today ? today : minDate
    },
    maxDate: function () {
      if (!this.coveredPeriod) return null

      return new Date(this.coveredPeriod.end)
    },
    shopLink: function () {
      return this.$router.resolve({ name: this.shopConfig.mixedShopError.link, params: { lang: 'nl' } }).href
    },
    configurationForCustomerGroupAndTerm: function () {
      if (!this.term) return null
      if (!this.customerGroup) return null
      return this.configurations.find(
        configuration => configuration.term.id === this.term.id &&
          configuration.customerGroup.id === this.customerGroup.id
      )
    },
    supportsSubscriptions: function () {
      return this.configurationForCustomerGroupAndTerm
        ? this.configurationForCustomerGroupAndTerm.supportsSubscriptions
        : false
    },
    isStarProduct: function () { return this.product.__typename === 'StarProduct' },
    isRouteProduct: function () { return this.product.__typename === 'RouteProduct' },
    isRegularProduct: function () { return this.product.__typename === 'RegularProduct' },
    engravedIdErrorCode: function () {
      if (this.hasErrorForCode(CARD_NUMBER_INVALID)) return CARD_NUMBER_INVALID
      if (this.hasErrorForCode(CARD_NOT_FOUND_AT_TLS)) return CARD_NOT_FOUND_AT_TLS
      if (this.hasErrorForCode(CARD_HAS_WRONG_TYPE)) return CARD_HAS_WRONG_TYPE
      if (this.hasErrorForCode(CARD_NOT_ACTIVE_AT_TLS)) return CARD_NOT_ACTIVE_AT_TLS
      if (this.hasErrorForCode(CARD_LOCALLY_BLACKLISTED)) return CARD_LOCALLY_BLACKLISTED
      if (this.hasErrorForCode(CARD_HAS_EXPIRED)) return CARD_HAS_EXPIRED
      if (this.hasErrorForCode(BASE_PROPOSITION_ALREADY_REGISTERED)) return BASE_PROPOSITION_ALREADY_REGISTERED
      if (this.hasErrorForCode(ORDER_HAS_PRODUCT_CONFLICT_CRITICAL)) return ORDER_HAS_PRODUCT_CONFLICT_CRITICAL
      if (this.hasErrorForCode(ORDER_HAS_PRODUCT_CONFLICT_HIGH)) return ORDER_HAS_PRODUCT_CONFLICT_HIGH
      if (this.hasErrorForCode(ORDER_HAS_PRODUCT_CONFLICT_MEDIUM)) return ORDER_HAS_PRODUCT_CONFLICT_MEDIUM

      return null
    },
    termErrorCode: function () {
      let result = null
      // first collect all data we depend upon, before determining what to return. This to prevent that Vue misses some dependencies.
      // see http://optimizely.github.io/vuejs.org/guide/computed.html#Dependency_Collection_Gotcha
      if (this.hasErrorForCode(TARIFF_NOT_PERMITTED_FOR_CARD)) result = TARIFF_NOT_PERMITTED_FOR_CARD
      if (this.hasErrorForCode(ORDER_HAS_DUPLICATE_ITEMS)) result = ORDER_HAS_DUPLICATE_ITEMS
      if (this.hasErrorForCode(ORDER_IS_MIXED)) result = ORDER_IS_MIXED
      if (this.hasErrorForCode(CUSTOMER_GROUP_NOT_MATCH_CARD_HOLDER)) result = CUSTOMER_GROUP_NOT_MATCH_CARD_HOLDER

      // if the card is not accepted, term specific messages are not relevant
      if (this.engravedIdErrorCode) {
        result = null
      }

      return result
    },
    mixedShopItemsErrorCode: function () {
      return !!this.hasErrorForCode(ORDER_HAS_MIXED_SHOP_ITEMS)
    },
    configurationErrorCode: function () {
      let code = null
      if (this.orderApiErrorResponse && this.orderApiErrorResponse.errors) {
        code = this.orderApiErrorResponse.errors[0].code
      }
      if (this.hasErrorForCode(NO_MATCHING_PRICE_FOR_CONFIGURATION)) {
        code = NO_MATCHING_PRICE_FOR_CONFIGURATION
      }
      if (this.hasErrorForCode(ORDER_NOT_ELIGIBLE_FOR_SPECIAL_SALE)) {
        code = ORDER_NOT_ELIGIBLE_FOR_SPECIAL_SALE
      }
      if (this.engravedIdErrorCode || this.termErrorCode || this.mixedShopItemsErrorCode) {
        code = null
      }

      return code
    }
  },
  watch: {
    supportsSubscriptions: function (newValue) {
      if (newValue === false) this.$emit('update:expiryReminder', false)
      if (newValue === true && this.term) this.$emit('update:expiryReminder', true)
    },
    customerGroup: function () {
      // reset add to cart button when customer group selection changes
      this.addToCartDisabled = false
    },
    termErrorCode: function (newValue) {
      this.satisfiableByCustomerGroup = null
      if (newValue) {
        const error = this.orderApiErrorResponse.errors.find(
          error => error.code === CUSTOMER_GROUP_NOT_MATCH_CARD_HOLDER
        )

        if (error && error.satisfiableBy && error.satisfiableBy.validCustomerGroups) {
          const otherErrors = this.orderApiErrorResponse.errors.filter(
            error => error.code !== CUSTOMER_GROUP_NOT_MATCH_CARD_HOLDER
          )
          if (otherErrors.length === 0) {
            const customerGroup = this.customerGroupsToShow.find(
              customerGroup => error.satisfiableBy.validCustomerGroups.find(
                validCustomerGroup => {
                  return validCustomerGroup.slug
                    ? String(validCustomerGroup.slug) === customerGroup.deeplinkId
                    : String(validCustomerGroup.id) === customerGroup.deeplinkId
                }
              )
            )

            if (customerGroup) {
              this.satisfiableByCustomerGroup = customerGroup
              this.setCustomerGroup(customerGroup)
            }
          }
        }
      }
    }
  },
  methods: {
    setSelectedTerm: function (value) {
      this.addToCartDisabled = false
      this.$store.commit('configuration/' + SET_SELECTED_TERM, value)
    },
    setEngravedId: function (value) {
      this.addToCartDisabled = false
      this.$store.commit('configuration/' + SET_ENGRAVED_ID, value)
    },
    setCustomerGroup: function (value) {
      this.addToCartDisabled = false
      this.$store.commit('configuration/' + SET_SELECTED_CUSTOMER_GROUP, value)
    },
    setStarValue: function (value) {
      this.addToCartDisabled = false
      this.$store.commit('configuration/' + SET_SELECTED_STAR_VALUE, value)
    },
    setClass: function (value) {
      this.addToCartDisabled = false
      this.$store.commit('configuration/' + SET_SELECTED_CLASS, value)
    },
    setZone: function (value) {
      this.addToCartDisabled = false
      this.$store.commit('configuration/' + SET_SELECTED_ZONE, value)
    },
    setToStop: function (value) {
      this.addToCartDisabled = false
      this.$store.commit('configuration/' + SET_SELECTED_TO_STOP, value)
    },
    setFromStop: function (value) {
      this.addToCartDisabled = false
      this.$store.commit('configuration/' + SET_SELECTED_FROM_STOP, value)
    },
    setExpiryReminder: function (value) {
      this.addToCartDisabled = false
      this.$store.commit('configuration/' + SET_EXPIRY_REMINDER, value)
    },
    setDateEffective: function (value) {
      this.addToCartDisabled = false
      this.$store.commit('configuration/' + SET_DATE_EFFECTIVE, formatStoreDate(value))
    },
    setAgreeToTerms: function (value) {
      this.addToCartDisabled = false
      this.agreeToTerms = value
    },
    validateForm: async function (e) {
      e.target.parentElement.blur()
      this.orderApiErrorResponse = null
      this.$emit('update:configurationInvalid', false)
      this.$emit('update:serviceError', false)
      this.$v.$touch()
      if (!this.$v.$invalid) {
        try {
          const orderItem = buildOrderItem()
          const orderItemKey = orderItem.id
          orderItem.productConfigurationCode = this.configurationForCustomerGroupAndTerm.orderCode
          orderItem.engravedId = this.engravedId
          orderItem.startDate = formatOrderApiDate(parseStoreDate(this.dateEffective))
          orderItem.title = this.getOrderItemTitle()
          orderItem.renewSubscriptionId = this.renewSubscriptionId
          orderItem.receiveSubscriptionReminders = this.expiryReminder
          orderItem.specialSaleSlug = this.specialSaleSlug

          const order = buildOrder()
          order.items = { ...this.$store.getters['cart/order'].items }
          order.items[orderItemKey] = orderItem

          await this.$store.dispatch('cart/assertOrderValid', { order })

          const indexedOrderItemObj = {}
          indexedOrderItemObj[orderItemKey] = orderItem
          this.addToCartDisabled = true
          await this.$store.dispatch('cart/addOrderItem', indexedOrderItemObj)
        } catch (error) {
          this.$log.debug(error)
          if (error instanceof Error) {
            if (error.hasOwnProperty('errors')) {
              this.orderApiErrorResponse = error
              this.$emit('update:configurationInvalid', this.configurationErrorCode)
            } else {
              this.$emit('update:serviceError', true)
            }
          }
        }
      }
    },
    goToCart: async function () {
      await this.$store.commit('configuration/' + RESET)
      this.$router.push({ name: 'Cart' }).catch(() => {})
    },
    getOrderItemTitle: function () {
      const title = this.configurationForCustomerGroupAndTerm.title

      if (this.isStarProduct) {
        return this.$t('product-detail.order-item-title.star-product', {
          title: title,
          zone: this.zone.title,
          starValue: this.starValue.title
        })
      }

      if (this.isRouteProduct) {
        const fromStop = this.stops.find(stop => stop.id === this.fromStop.id)
        const toStop = this.stops.find(stop => stop.id === this.toStop.id)

        return this.$t('product-detail.order-item-title.route-product', {
          title: title,
          fromStop: fromStop.title,
          toStop: toStop.title
        })
      }

      return title
    },
    hasErrorForCode: function (code) {
      if (!this.orderApiErrorResponse) return false

      return this.orderApiErrorResponse.errors.some(error => error.code === code)
    }
  },
  validations: {
    engravedId: {
      required,
      regex: regex('regex', /^(?!352806).*/),
      minLength: minLength(16),
      errorCode () { return withParams({ code: this.engravedIdErrorCode }, () => !this.engravedIdErrorCode)() }
    },
    customerGroup: {
      required
    },
    zone: {
      required: requiredIf('isStarProduct')
    },
    starValue: {
      required: requiredIf('isStarProduct')
    },
    fromStop: {
      required: requiredIf('isRouteProduct')
    },
    toStop: {
      required: requiredIf('isRouteProduct')
    },
    selectedClass: {
      required
    },
    term: {
      required,
      hasConfiguration: function () { return typeof this.configurationForCustomerGroupAndTerm !== 'undefined' },
      errorCode () {
        return withParams(
          { code: this.termErrorCode, satisfiableByCustomerGroup: this.satisfiableByCustomerGroup },
          () => !this.termErrorCode
        )()
      }
    },
    dateEffective: {
      required
    },
    agreeToTerms: {
      required
    },
    button: {
      errorCode () {
        return withParams(
          { code: this.mixedShopItemsErrorCode }, () => !this.mixedShopItemsErrorCode
        )
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/forms/OrderItem.vue